/**
 * LeadImportModal - CSV file upload and import
 */

import { useState, useRef } from "react";
import { Modal } from "@/components/ui/Modal";
import { usePreviewCSV, useImportLeadsCSV } from "../hooks/useLeads";
import type { CSVPreviewResult, CSVImportResult } from "../Lead.types";

interface LeadImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: (result: CSVImportResult) => void;
}

export function LeadImportModal({
  isOpen,
  onClose,
  onImportComplete,
}: LeadImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CSVPreviewResult | null>(null);
  const [importResult, setImportResult] = useState<CSVImportResult | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [city, setCity] = useState("");
  const [step, setStep] = useState<"upload" | "preview" | "result">("upload");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewMutation = usePreviewCSV();
  const importMutation = useImportLeadsCSV();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      alert("Please select a CSV file");
      return;
    }

    setFile(selectedFile);
    setPreview(null);

    // Preview the file
    try {
      const result = await previewMutation.mutateAsync({ file: selectedFile });
      setPreview(result);
      setStep("preview");
    } catch (error) {
      console.error("Preview failed:", error);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      const result = await importMutation.mutateAsync({
        file,
        skipDuplicates,
        city: city.trim() || undefined,
      });
      setImportResult(result);
      setStep("result");
      onImportComplete?.(result);
    } catch (error) {
      console.error("Import failed:", error);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setCity("");
    setStep("upload");
    onClose();
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setCity("");
    setStep("upload");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Leads from CSV"
      size="lg"
    >
      <div className="p-6">
        {step === "upload" && (
          <div className="text-center">
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg
                className="w-12 h-12 mx-auto text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-gray-600 mb-2">
                Click to select a CSV file or drag and drop
              </p>
              <p className="text-sm text-gray-400">
                The file should contain lead information with headers
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />

            {previewMutation.isPending && (
              <div className="mt-4 flex items-center justify-center gap-2 text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                <span>Analyzing file...</span>
              </div>
            )}

            <div className="mt-6 text-left">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Supported columns:
              </h4>
              <div className="grid grid-cols-2 gap-1 text-xs text-gray-500">
                <span>name, business name, company</span>
                <span>sector, industry</span>
                <span>email, email address</span>
                <span>phone, tel</span>
                <span>website, url</span>
                <span>address, location</span>
                <span>instagram, ig</span>
                <span>google, google maps</span>
                <span>description, notes</span>
                <span>country</span>
              </div>
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                File: <span className="font-medium">{file?.name}</span>
              </p>
              <p className="text-sm text-gray-600">
                Preview: {preview.totalPreviewRows} rows shown
              </p>
            </div>

            {/* Field mappings */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Detected Field Mappings
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {preview.fieldMappings.map((mapping, idx) => (
                  <div
                    key={idx}
                    className={`text-xs px-2 py-1 rounded ${
                      mapping.mapped
                        ? "bg-green-50 text-green-700"
                        : "bg-gray-50 text-gray-500"
                    }`}
                  >
                    {mapping.csvHeader}
                    {mapping.mapped && (
                      <span className="text-green-600"> = {mapping.leadField}</span>
                    )}
                  </div>
                ))}
              </div>

              {preview.unmappedHeaders.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Unmapped columns will be ignored: {preview.unmappedHeaders.join(", ")}
                </p>
              )}
            </div>

            {/* Sample data */}
            <div className="mb-4 overflow-x-auto">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Sample Data
              </h4>
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Name</th>
                    <th className="px-2 py-1 text-left">Sector</th>
                    <th className="px-2 py-1 text-left">IG Followers</th>
                    <th className="px-2 py-1 text-left">Google Rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.sampleRows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="px-2 py-1">{row.mapped.name as string || "-"}</td>
                      <td className="px-2 py-1">{row.mapped.sector as string || "-"}</td>
                      <td className="px-2 py-1">{row.mapped.instagramFollowers as number || "-"}</td>
                      <td className="px-2 py-1">{row.mapped.googleRating as number || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Options */}
            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City (applied to all imports)
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g., Bologna, Milano, Roma"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  className="rounded-sm border-gray-300"
                />
                Skip duplicates (same name + address)
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Choose Different File
              </button>
              <button
                onClick={handleImport}
                disabled={importMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {importMutation.isPending ? "Importing..." : "Import Leads"}
              </button>
            </div>
          </div>
        )}

        {step === "result" && importResult && (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Import Complete
            </h3>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-green-600">{importResult.created}</p>
                <p className="text-sm text-green-700">Created</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-yellow-600">{importResult.skipped}</p>
                <p className="text-sm text-yellow-700">Skipped</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-red-600">{importResult.errors.length}</p>
                <p className="text-sm text-red-700">Errors</p>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div className="mb-4 text-left">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Errors:</h4>
                <div className="max-h-32 overflow-y-auto text-xs text-red-600 bg-red-50 rounded-sm p-2">
                  {importResult.errors.map((error, idx) => (
                    <p key={idx}>
                      Row {error.row}: {error.error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
